import { useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { reportsApi } from '../api/reports'
import { useAuth } from '../contexts/AuthContext'
import type { Investigation } from '../lib/types'
import { formatDateBR, formatDateTimeBR } from '../lib/format'
import type { ManagerQuestion } from '../lib/types'
import { OptimizationChecklist } from '../components/OptimizationChecklist'
import { InvestigationCard } from '../components/InvestigationCard'
import { ChatPanel } from '../components/ChatPanel'
import { ExportPdfButton } from '../components/ExportPdfButton'
import { QuestionItem } from '../components/IntelligenceInbox'
import { Spinner } from '../components/Spinner'
import { PublishModal } from '../components/portal/PublishModal'
import { portalAdminApi } from '../api/portalAdmin'

/** Perguntas que a IA fez a partir deste relatório: abertas (1 clique) e já respondidas. */
function ReportQuestions({ questions }: { questions: ManagerQuestion[] }) {
  if (questions.length === 0) return null
  const open = questions.filter((q) => q.status === 'open')
  const done = questions.filter((q) => q.status !== 'open')
  return (
    <section className="rounded-lg border border-violet-200 bg-violet-50/40 p-4">
      <h3 className="mb-1 font-semibold text-slate-900">🧠 Perguntas da IA desta semana</h3>
      <p className="mb-3 text-xs text-slate-500">
        Respostas rápidas viram conhecimento do cliente (dossiê, metas e qualidade do lead) que a IA usa nas
        próximas análises.
      </p>
      {open.length > 0 && (
        <ul className="space-y-2">
          {open.map((q) => (
            <QuestionItem key={q.id} q={q} showClient={false} />
          ))}
        </ul>
      )}
      {done.length > 0 && (
        <ul className="mt-3 space-y-1 text-sm">
          {done.map((q) => (
            <li key={q.id} className="text-slate-600">
              <span className="text-slate-500">{q.question}</span>{' '}
              {q.status === 'answered' ? (
                <strong className="text-slate-900">→ {q.answer}</strong>
              ) : (
                <em className="text-slate-400">(não se aplica)</em>
              )}
              {q.answeredBy && <span className="text-xs text-slate-400"> · {q.answeredBy}</span>}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function isProcessing(investigations: Investigation[]): boolean {
  return investigations.some(
    (i) =>
      i.status === 'ai_running' ||
      (i.type === 'manager_manual' && !!i.managerFindings && i.status !== 'manager_complete')
  )
}

type Tab = 'relatorio' | 'quinzenal' | 'acoes' | 'investigacoes' | 'discutir'

export default function ReportDetail() {
  const { id } = useParams()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [tab, setTab] = useState<Tab>('relatorio')
  // Blocos de saída da IA exportáveis para PDF (análise semanal e quinzenal).
  const analysisRef = useRef<HTMLDivElement>(null)
  const biweeklyRef = useRef<HTMLDivElement>(null)

  const { data: report, isLoading, isError } = useQuery({
    queryKey: ['report', id],
    queryFn: () => reportsApi.get(id!),
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return false
      if (data.status === 'processing') return 2000
      return isProcessing(data.investigations) ? 3000 : false
    },
  })

  // Relatórios do mesmo cliente (ordenados por weekEnd desc no backend), para
  // navegar Anterior/Próximo. Só dispara quando já sabemos o cliente.
  const clientId = report?.client.id
  const { data: siblingsData } = useQuery({
    queryKey: ['report-siblings', clientId],
    queryFn: () => reportsApi.list({ clientId: clientId!, read: 'all' }),
    enabled: !!clientId,
  })

  const markRead = useMutation({
    mutationFn: () => reportsApi.markRead(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report', id] }),
  })
  const regenSummary = useMutation({
    mutationFn: () => reportsApi.regenerateSummary(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report', id] }),
  })
  const clearSummary = useMutation({
    mutationFn: () => reportsApi.clearSummary(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report', id] }),
  })
  const publish = useMutation({
    mutationFn: () => reportsApi.publish(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report', id] }),
  })
  const remove = useMutation({
    mutationFn: () => reportsApi.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reports'] })
      navigate('/reports')
    },
  })
  // Gera o quinzenal e recarrega o relatório (o conteúdo é persistido no backend).
  const biweekly = useMutation({
    mutationFn: () => reportsApi.biweekly(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report', id] }),
  })
  // Publicar o quinzenal (revisado) no portal do cliente.
  const [publishOpen, setPublishOpen] = useState(false)
  const portalPublish = useMutation({
    mutationFn: (a: { title: string; content: string }) => portalAdminApi.publishBiweekly(id!, a.title, a.content),
  })

  if (isLoading) return <Spinner />
  if (isError || !report)
    return <p className="text-sm text-red-600">Não foi possível carregar o relatório.</p>

  // Geração em andamento — a página se atualiza sozinha (polling).
  if (report.status === 'processing') {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold text-slate-900">{report.client.name}</h1>
        <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-5 text-amber-800">
          <Spinner className="h-5 w-5 text-amber-600" />
          <div>
            <p className="font-semibold">Gerando relatório…</p>
            <p className="text-sm">
              Buscando os dados no Windsor e rodando a análise do Claude. Isso pode levar de alguns
              segundos a alguns minutos. Esta página atualiza sozinha.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Falha na geração — mostra o erro e permite remover.
  if (report.status === 'error') {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h1 className="text-2xl font-bold text-slate-900">{report.client.name}</h1>
          {isAdmin && (
            <button
              onClick={() => {
                if (confirm('Remover este relatório com falha?')) remove.mutate()
              }}
              className="rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
            >
              Remover
            </button>
          )}
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-red-800">
          <p className="font-semibold">Falha ao gerar o relatório</p>
          <p className="mt-1 text-sm">
            Tente novamente em "Clientes → Gerar relatório". Se persistir, verifique as chaves de API
            e os dados do cliente.
          </p>
          {report.errorMessage && (
            <pre className="mt-3 overflow-auto rounded-md bg-white/70 px-3 py-2 text-xs text-red-700">
              {report.errorMessage}
            </pre>
          )}
        </div>
      </div>
    )
  }

  // Vizinhos na lista (weekEnd desc → mais recente primeiro).
  const siblings = siblingsData?.reports ?? []
  const idx = siblings.findIndex((r) => r.id === report.id)
  const olderReport = idx >= 0 ? siblings[idx + 1] : undefined // semana anterior
  const newerReport = idx > 0 ? siblings[idx - 1] : undefined // semana seguinte

  const tabs: Array<{ key: Tab; label: string }> = [
    { key: 'relatorio', label: 'Relatório' },
    { key: 'quinzenal', label: report.biweekly ? 'Quinzenal ✓' : 'Quinzenal' },
    { key: 'acoes', label: `Ações (${report.optimizations.length})` },
    {
      key: 'investigacoes',
      label: `Investigações (${report.investigations.length})${
        (report.questions ?? []).some((q) => q.status === 'open') ? ' · 🧠 perguntas' : ''
      }`,
    },
    { key: 'discutir', label: '💬 Discutir sobre o relatório' },
  ]

  return (
    <div className="space-y-6">
      {/* Barra fixa: cliente + período sempre visíveis + navegação entre relatórios */}
      <div className="sticky top-0 z-10 -mx-4 -mt-4 mb-1 border-b border-slate-200 bg-white/85 px-4 py-2.5 backdrop-blur-md md:-mx-6 md:-mt-6 md:px-6 print:hidden">
        <div className="flex items-center gap-3">
          <button
            onClick={() => olderReport && navigate(`/reports/${olderReport.id}`)}
            disabled={!olderReport}
            title={
              olderReport
                ? `${formatDateBR(olderReport.weekStart)} a ${formatDateBR(olderReport.weekEnd)}`
                : 'Não há relatório anterior'
            }
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <span aria-hidden>←</span>
            <span className="hidden sm:inline">Anterior</span>
          </button>

          <div className="min-w-0 flex-1 text-center">
            <div className="truncate text-base font-bold text-slate-900">{report.client.name}</div>
            <div className="text-xs text-slate-500">
              {formatDateBR(report.weekStart)} a {formatDateBR(report.weekEnd)}
            </div>
          </div>

          <button
            onClick={() => newerReport && navigate(`/reports/${newerReport.id}`)}
            disabled={!newerReport}
            title={
              newerReport
                ? `${formatDateBR(newerReport.weekStart)} a ${formatDateBR(newerReport.weekEnd)}`
                : 'Não há relatório seguinte'
            }
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <span className="hidden sm:inline">Próximo</span>
            <span aria-hidden>→</span>
          </button>
        </div>
      </div>

      {/* Cabeçalho */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            {/* Título completo só na impressão — na tela quem mostra cliente+período
                é a barra fixa acima (evita duplicar). */}
            <div className="hidden print:block">
              <h1 className="text-2xl font-bold text-slate-900">{report.client.name}</h1>
              <p className="text-sm text-slate-500">
                {formatDateBR(report.weekStart)} a {formatDateBR(report.weekEnd)}
              </p>
            </div>
            <p className="text-sm text-slate-500">
              gerado em {formatDateTimeBR(report.generatedAt)}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                  report.status === 'published'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                {report.status === 'published' ? 'Publicado' : 'Rascunho'}
              </span>
              {report.readBy.length > 0 && (
                <span className="text-xs text-slate-500">
                  Lido por: {report.readBy.map((r) => r.name).join(', ')}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 print:hidden">
            <button
              onClick={() => window.print()}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Exportar para PDF
            </button>
            {!report.isRead && (
              <button
                onClick={() => markRead.mutate()}
                disabled={markRead.isPending}
                className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Marcar como lido
              </button>
            )}
            {report.isRead && (
              <span className="inline-flex items-center rounded-md bg-green-50 px-3 py-1.5 text-sm text-green-700">
                Lido ✓
              </span>
            )}
            {isAdmin && report.status === 'draft' && (
              <button
                onClick={() => publish.mutate()}
                disabled={publish.isPending}
                className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
              >
                Publicar
              </button>
            )}
            {isAdmin && (
              <button
                onClick={() => {
                  if (confirm('Remover este relatório? Esta ação não pode ser desfeita.'))
                    remove.mutate()
                }}
                className="rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                Remover
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Abas */}
      <div className="flex gap-1 overflow-x-auto border-b border-slate-200 print:hidden">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              tab === t.key
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Aba: Relatório (análise + avisos + resumo de memória) ── */}
      {tab === 'relatorio' && (
        <div className="space-y-6">
          <section className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-bold text-slate-900">Análise comparativa</h2>
              <ExportPdfButton
                targetRef={analysisRef}
                title={`Análise comparativa — ${report.client.name}`}
                subtitle={`Semana de ${formatDateBR(report.weekStart)} a ${formatDateBR(
                  report.weekEnd
                )} · gerado em ${formatDateTimeBR(report.generatedAt)}`}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 print:hidden"
              />
            </div>
            <div ref={analysisRef} className="markdown border-t border-slate-100 pt-3">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.analysis}</ReactMarkdown>
            </div>
          </section>

          {report.ga4Fallback && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <span className="font-semibold">*</span> A análise do site (GA4) falhou na primeira
              tentativa de geração e foi <strong>removida</strong> deste relatório para concluí-lo
              (pode ter sido instabilidade momentânea). Para tentar incluí-la novamente, gere o
              relatório de novo.
            </p>
          )}

          {isAdmin && (
            <section className="rounded-xl border border-slate-200 bg-white p-5 print:hidden">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-base font-semibold text-slate-900">Resumo de memória (admin)</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => regenSummary.mutate()}
                    disabled={regenSummary.isPending}
                    className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                  >
                    {regenSummary.isPending && <Spinner className="h-4 w-4 text-white" />}
                    Regenerar agora
                  </button>
                  <button
                    onClick={() => clearSummary.mutate()}
                    disabled={clearSummary.isPending}
                    className="rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    Tornar null
                  </button>
                </div>
              </div>
              <p className="mb-2 text-xs text-slate-500">
                Resumo compacto usado como <strong>memória</strong> na geração dos próximos
                relatórios deste cliente. "Regenerar" refaz com o modelo atual; "Tornar null" apaga
                (regenera na próxima geração).
              </p>
              {regenSummary.isError && (
                <p className="mb-2 text-xs text-red-600">Erro ao regenerar o resumo.</p>
              )}
              {report.learningSummary ? (
                <>
                  <pre className="whitespace-pre-wrap rounded-md bg-slate-50 p-3 text-sm text-slate-700">
                    {report.learningSummary}
                  </pre>
                  <p className="mt-1 text-xs text-slate-400">
                    {report.learningSummary.length} caracteres
                  </p>
                </>
              ) : (
                <p className="text-sm text-slate-400">
                  — sem resumo — (será gerado na próxima geração deste cliente)
                </p>
              )}
            </section>
          )}
        </div>
      )}

      {/* ── Aba: Relatório quinzenal (voltado ao cliente, persistido) ── */}
      {tab === 'quinzenal' && (
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-bold text-slate-900">
              Relatório quinzenal (voltado ao cliente)
              {report.biweekly?.periodLabel ? ` — ${report.biweekly.periodLabel}` : ''}
            </h2>
            <div className="flex items-center gap-2">
              {report.biweekly && (
                <>
                  <button
                    onClick={() => navigator.clipboard?.writeText(report.biweekly!.content)}
                    className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                  >
                    Copiar
                  </button>
                  <ExportPdfButton
                    targetRef={biweeklyRef}
                    title={`Relatório quinzenal — ${report.client.name}`}
                    subtitle={
                      report.biweekly.periodLabel ??
                      `Semana de ${formatDateBR(report.weekStart)} a ${formatDateBR(report.weekEnd)}`
                    }
                    className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                  />
                  <button
                    onClick={() => {
                      portalPublish.reset()
                      setPublishOpen(true)
                    }}
                    title="Revisar e publicar este texto no portal do cliente"
                    className="rounded-md bg-brand-600 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-700"
                  >
                    Publicar para o cliente
                  </button>
                </>
              )}
              <button
                onClick={() => biweekly.mutate()}
                disabled={biweekly.isPending}
                title="Gera, via IA, um texto consolidado (este relatório + o anterior do cliente) voltado ao cliente final — sem jargão interno."
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {biweekly.isPending && <Spinner className="h-4 w-4" />}
                {report.biweekly ? 'Regerar' : 'Gerar'}
              </button>
            </div>
          </div>

          {biweekly.isError && (
            <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              Não foi possível gerar o relatório quinzenal:{' '}
              {biweekly.error instanceof Error ? biweekly.error.message : 'erro desconhecido'}
            </p>
          )}
          {biweekly.isPending && (
            <div className="flex items-center gap-3 rounded-lg border border-brand-200 bg-brand-50/40 p-4 text-sm text-brand-800">
              <Spinner className="h-5 w-5 text-brand-600" />
              Gerando o relatório quinzenal via IA (consolida este relatório com o anterior do
              cliente). Pode levar até ~30s…
            </div>
          )}

          {report.biweekly ? (
            <>
              <div ref={biweeklyRef} className="markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.biweekly.content}</ReactMarkdown>
              </div>
              {report.biweekly.generatedAt && (
                <p className="mt-3 text-xs text-slate-400">
                  Gerado em {formatDateTimeBR(report.biweekly.generatedAt)}
                </p>
              )}
            </>
          ) : (
            !biweekly.isPending && (
              <p className="text-sm text-slate-500">
                Ainda não gerado. Clique em "Gerar" para criar o texto voltado ao cliente; depois, revise e publique no
                portal do cliente.
              </p>
            )
          )}
          {report.biweekly && (
            <PublishModal
              open={publishOpen}
              heading={`Publicar no portal — ${report.client.name}`}
              initialTitle={`Relatório quinzenal${report.biweekly.periodLabel ? ` — ${report.biweekly.periodLabel}` : ''}`}
              initialContent={report.biweekly.content}
              publishing={portalPublish.isPending}
              error={portalPublish.error instanceof Error ? portalPublish.error.message : null}
              done={portalPublish.isSuccess}
              onClose={() => setPublishOpen(false)}
              onPublish={(title, content) => portalPublish.mutate({ title, content })}
            />
          )}
        </section>
      )}

      {/* ── Aba: Ações de otimização ── */}
      {tab === 'acoes' && (
        <OptimizationChecklist optimizations={report.optimizations} reportId={report.id} />
      )}

      {/* ── Aba: Investigações (+ perguntas objetivas da IA desta semana) ── */}
      {tab === 'investigacoes' && (
        <div className="space-y-4">
          <ReportQuestions questions={report.questions ?? []} />
          {report.investigations.map((inv) => (
            <InvestigationCard key={inv.id} investigation={inv} reportId={report.id} />
          ))}
          {report.investigations.length === 0 && (
            <p className="text-sm text-slate-500">Nenhuma investigação para este relatório.</p>
          )}
        </div>
      )}

      {/* ── Aba: Discutir sobre o relatório (chat com o conteúdo deste relatório) ── */}
      {tab === 'discutir' && (
        <ChatPanel
          clientId={report.client.id}
          reportId={report.id}
          className="h-[65vh]"
          exportTitle={`Discussão do relatório — ${report.client.name}`}
          exportSubtitle={`Semana de ${formatDateBR(report.weekStart)} a ${formatDateBR(
            report.weekEnd
          )}`}
          emptyHint={
            <>
              Discuta <strong>este relatório</strong> com a IA — ela já tem o conteúdo completo
              (análise, ações + feedback do gerente, investigações). Para números de outras semanas
              ou KPIs históricos, ela busca com as ferramentas. Ex.: "Por que o CPA subiu nesta
              semana?" ou "As ações recomendadas fazem sentido dado o histórico?".
            </>
          }
        />
      )}
    </div>
  )
}
